import { Avatar, Loader, SidebarOverlay } from "@bfc-software/pallet";

import type { PickersDetail } from "../-data/dashboard-model";
import { useSelectorDirectory } from "../../tasks/-data/use-selector-directory";
import type { DashboardT } from "./dashboard-t";
import styles from "./dashboard.module.css";

/**
 * Who is on shift and what each is working: the drawer behind the Pickers card.
 *
 * A row per claimed task of an active selector, so a selector holding two
 * appears twice, then the active selectors with nothing in hand, then the idle
 * ones (no device action for ten minutes). Read-only; the design's
 * validation badges and overrides wait on data nothing serves yet.
 *
 * Mounted only while open (the caller renders it conditionally), which is what
 * keeps the name directory's two reads off the dashboard until someone asks.
 * Under `control: "external"` the constant `isOpen` and the `onClose` routed
 * from every dismissal are the whole open/closed state, as in the task drawer.
 */
export function PickersDrawer({
  detail,
  onClose,
  t,
}: {
  detail: PickersDetail;
  onClose: () => void;
  t: DashboardT;
}) {
  const names = useSelectorDirectory();
  const nameOf = (selectorId: string) =>
    names.data?.get(selectorId) ?? t("dashboard.pickers.drawer.unknown");
  const selectorsActive =
    new Set(detail.picking.map((task) => task.selectorId)).size +
    detail.waiting.length;

  return (
    <SidebarOverlay
      control="external"
      isOpen={true}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
      header={
        <>
          <h2 className="drawer-title">
            {t("dashboard.pickers.drawer.title")}
          </h2>
          <p className="drawer-subtitle">
            {t("dashboard.pickers.drawer.activeNow", {
              count: selectorsActive,
            })}
          </p>
        </>
      }
    >
      <div className="drawer-body" data-test-role="pickers-drawer">
        {names.isPending && <Loader />}
        {names.isError && (
          <p role="alert" className={styles.note}>
            {t("dashboard.pickers.drawer.failed")}
          </p>
        )}
        {!names.isPending && (
          <>
            {selectorsActive === 0 ? (
              <p className={styles.note}>
                {t("dashboard.pickers.drawer.nobody")}
              </p>
            ) : (
              <div role="list" className={styles.pickerList}>
                {detail.picking.map((task) => (
                  <div
                    key={`${task.selectorId}-${task.taskNumber}`}
                    role="listitem"
                    className={styles.pickerRow}
                    data-test-role="picker-row"
                  >
                    <Avatar
                      name={nameOf(task.selectorId)}
                      colorKey={task.selectorId}
                    />
                    <div className={styles.pickerText}>
                      <span className={styles.pickerName}>
                        {nameOf(task.selectorId)}
                      </span>
                      <span className={styles.pickerMeta}>
                        {[
                          t("dashboard.pickers.drawer.task", {
                            id: task.taskNumber,
                          }),
                          task.zones.join(", "),
                          t("dashboard.pickers.drawer.progress", {
                            done: task.done,
                            total: task.total,
                          }),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </div>
                  </div>
                ))}
                {detail.waiting.map((selectorId) => (
                  <div
                    key={selectorId}
                    role="listitem"
                    className={styles.pickerRow}
                    data-test-role="picker-waiting-row"
                  >
                    <Avatar name={nameOf(selectorId)} colorKey={selectorId} />
                    <div className={styles.pickerText}>
                      <span className={styles.pickerName}>
                        {nameOf(selectorId)}
                      </span>
                      <span className={styles.pickerMeta}>
                        {t("dashboard.pickers.drawer.waitingHint")}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {detail.idle.length > 0 && (
              <>
                <h3 className="section-heading">
                  {t("dashboard.pickers.drawer.idleHeading")}
                  {" · "}
                  {detail.idle.length}
                </h3>
                <div role="list" className={styles.pickerList}>
                  {detail.idle.map((selectorId) => (
                    <div
                      key={selectorId}
                      role="listitem"
                      className={styles.pickerRow}
                      data-test-role="picker-idle-row"
                    >
                      <Avatar name={nameOf(selectorId)} colorKey={selectorId} />
                      <div className={styles.pickerText}>
                        <span className={styles.pickerName}>
                          {nameOf(selectorId)}
                        </span>
                        <span className={styles.pickerMeta}>
                          {t("dashboard.pickers.drawer.idleHint")}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </SidebarOverlay>
  );
}
